import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { useLocation, useNavigate } from "react-router-dom";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    AOS.init({ duration: 800, once: false });
  }, []);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Track active section on scroll
  useEffect(() => {
    if (pathname !== "/") return;

    const sections = [
      "home",
      "about",
      "schedule",
      "travel",
      "registry",
      "rsvp",
    ];

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  const navLinks = [
    { label: "Home", scrollTo: "home" },
    { label: "Schedule", scrollTo: "schedule" },
    { label: "Travel", scrollTo: "travel" },
    { label: "Registry", scrollTo: "registry" },
    { label: "RSVP", scrollTo: "rsvp" },
    { label: "FAQs", path: "/faq" },
  ];

  const isActiveLink = (link: { scrollTo?: string; path?: string }) => {
    if (link.path) {
      return pathname === link.path;
    }
    if (link.scrollTo && pathname === "/") {
      return activeSection === link.scrollTo;
    }
    return false;
  };

  const handleNavClick = (link: {
    label: string;
    scrollTo?: string;
    path?: string;
  }) => {
    if (link.path) {
      navigate(link.path);
    } else if (link.scrollTo) {
      if (pathname !== "/") {
        navigate("/", { state: { scrollTo: link.scrollTo } });
      } else {
        document
          .getElementById(link.scrollTo)
          ?.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsOpen(false);
  };

  return (
    <section className="w-full z-50 bg-white">
      {/* Top Banner */}
      <div className="bg-[#3d3d3d] text-white text-center py-3 px-4">
        <p className="text-xs md:text-sm tracking-wide">
          We are looking forward to celebrating with you! Please ensure you RSVP
          by 28th February. Thank you, Joseph & Jennifer
        </p>
      </div>

      {/* Main Header */}
      <header className="relative border-b border-gray-200">
        <div className="flex flex-col items-center py-8">
          {/* Center: Title with Gradient */}
          <h1
            onClick={() => handleNavClick({ label: "Home", scrollTo: "home" })}
            className="text-xl md:text-3xl lg:text-4xl font-medium cursor-pointer bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent"
          >
            JOSEPH & JENNIFER
          </h1>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-12 mt-8 text-sm">
            {navLinks.map((link) => (
              <p
                key={link.label}
                onClick={() => handleNavClick(link)}
                className={`relative cursor-pointer transition-colors ${
                  isActiveLink(link)
                    ? "text-gray-800 font-medium"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {link.label}
                {isActiveLink(link) && (
                  <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-gradient-to-r from-[#C49355] to-[#E8B46A]"></span>
                )}
              </p>
            ))}
          </nav>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden absolute right-6 top-1/2 -translate-y-1/2 text-gray-700 focus:outline-none"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {/* Mobile Menu Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4 }}
              className="fixed top-0 right-0 h-full w-4/5 max-w-sm z-50 bg-white shadow-xl border-l border-gray-100 flex flex-col"
            >
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-700"
                >
                  <X size={28} />
                </button>
              </div>

              <div className="flex justify-center items-center py-4 border-b border-gray-200">
                <h2
                  onClick={() =>
                    handleNavClick({ label: "Home", scrollTo: "home" })
                  }
                  className="text-2xl text-center tracking-[0.2em] cursor-pointer bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent"
                >
                  JOSEPH <br /> & <br /> JENNIFER
                </h2>
              </div>

              <div className="flex flex-col items-center space-y-6 py-10 text-center">
                {navLinks.map((link) => (
                  <p
                    key={link.label}
                    onClick={() => handleNavClick(link)}
                    className={`text-lg transition cursor-pointer ${
                      isActiveLink(link)
                        ? "text-gray-800 font-medium"
                        : "text-gray-600 hover:text-[#C49355]"
                    }`}
                  >
                    {link.label}
                  </p>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
